import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useRoles } from '@/hooks/useRoles';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { api, ApiError } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { MobileJoinQr } from './MobileJoinQr';
import { defaultMechanicRoleId, roleDefinitionLabel } from './staffRoleLabels';

/** Token path: a pending invitation the invitee accepts with a link. */
interface TokenInviteResponse {
  id: string;
  email: string;
  role: string;
  invitationToken: string;
  invitationUrl: string;
  /**
   * Mobile enrollment QR payload (#1244 §4.1). Optional so a portal that is
   * ahead of its staff-api mid-rollout renders the rest of the success view
   * instead of a blank QR.
   */
  mobileJoinUrl?: string;
  expiresAt: string;
  emailSent: boolean;
}

/**
 * Paya ID path (#1626): the account and the membership already exist, so there
 * is nothing to accept — the invitee just signs in. `emailSent` is optional
 * because a staff-api older than #2791 does not report it.
 */
interface PayaIdInviteResponse {
  id: string;
  email: string;
  role: string;
  status: 'active';
  signInUrl: string;
  emailSent?: boolean;
}

type InviteResponse = TokenInviteResponse | PayaIdInviteResponse;

function isPayaIdInvite(result: InviteResponse): result is PayaIdInviteResponse {
  return 'signInUrl' in result;
}

/** The link the success view shows, whichever path the backend took. */
function inviteLink(result: InviteResponse): string {
  return isPayaIdInvite(result) ? result.signInUrl : result.invitationUrl;
}

interface AddTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddTeamMemberDialog({ open, onClose, onSuccess }: AddTeamMemberDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { isDirty, markDirty } = useFormDirty(open);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inviteResult, setInviteResult] = useState<InviteResponse | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const { data: roleDefinitions, isLoading: rolesLoading, isError: rolesError } = useRoles();
  const assignableRoles = (roleDefinitions ?? []).filter((role) => role.isActive);

  useEffect(() => {
    if (!open) return;
    const active = (roleDefinitions ?? []).filter((role) => role.isActive);
    setRoleId((current) => {
      if (current && active.some((role) => role.id === current)) return current;
      return defaultMechanicRoleId(active);
    });
  }, [open, roleDefinitions]);

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setRoleId('');
    setError(null);
    setInviteResult(null);
    setLinkCopied(false);
  };

  const handleClose = () => {
    if (inviteResult) {
      onSuccess();
    }
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    setError(null);

    if (!email || !roleId) {
      setError(t('addMemberRequiredFields'));
      return;
    }

    setSaving(true);
    try {
      const result = await api.post<InviteResponse>('/users/invite', {
        email,
        roleId,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
      });
      setInviteResult(result);
      notify.success(t('inviteSent'), email);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const data = err.data as { message?: string } | undefined;
        if (data?.message?.includes('pending invitation')) {
          setError(t('invitePendingExists'));
        } else {
          setError(t('addMemberEmailExists'));
        }
      } else {
        setError(t('inviteFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteResult) return;
    try {
      await navigator.clipboard.writeText(inviteLink(inviteResult));
      setLinkCopied(true);
      notify.success(t('linkCopied'));
      setTimeout(() => { setLinkCopied(false); }, 2000);
    } catch {
      // Fallback: select the text
    }
  };

  // Success view — no form to track, use simple ResponsiveDialog
  if (inviteResult) {
    const payaId = isPayaIdInvite(inviteResult);
    // A Paya ID invite says "sent" only when the backend confirmed it; an older
    // staff-api that does not report delivery gets the neutral wording and no
    // warning, since the mail most likely did go out.
    const descKey = payaId
      ? inviteResult.emailSent === true
        ? 'inviteSentPayaIdDesc'
        : 'inviteCreatedPayaIdDesc'
      : 'inviteSentDesc';
    const noEmailKey = payaId
      ? inviteResult.emailSent === false
        ? 'inviteSentPayaIdNoEmail'
        : null
      : inviteResult.emailSent
        ? null
        : 'inviteSentNoEmail';

    return (
      <ResponsiveDialog
        open={open}
        onClose={handleClose}
        mobileTitle={t('inviteSent')}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Stack spacing={3} alignItems="center" sx={{ py: 3 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h6" textAlign="center">
              {t('inviteSent')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t(descKey)} <strong>{inviteResult.email}</strong>
            </Typography>

            {noEmailKey && (
              <Alert severity="info" sx={{ width: '100%' }}>
                {t(noEmailKey)}
              </Alert>
            )}

            <Box sx={{ width: '100%' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t(payaId ? 'signInLink' : 'invitationLink')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={inviteLink(inviteResult)}
                  size="small"
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                />
                <Tooltip title={linkCopied ? t('linkCopied') : t('copyLink')}>
                  <IconButton onClick={() => void handleCopyLink()} size="small" aria-label={t('copyLink')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {!payaId && inviteResult.mobileJoinUrl && (
              <>
                <Divider flexItem sx={{ width: '100%' }} />
                <MobileJoinQr
                  mobileJoinUrl={inviteResult.mobileJoinUrl}
                  email={inviteResult.email}
                  expiresAt={inviteResult.expiresAt}
                  // An invitation gets a copy button: an emailed link cannot be
                  // relied on to open the app (Gmail rewrites it), so the admin
                  // needs a way to hand the code over by any channel. See
                  // `CopyableCode` for why this is opt-in rather than default.
                  enableCopy
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleClose}>
            {tc('close')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    );
  }

  // Invite form view
  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={t('addMember')}
      isDirty={isDirty}
      onSubmit={() => void handleSave()}
      submitLabel={t('sendInvite')}
      submitDisabled={!email || !roleId || rolesLoading}
      isLoading={saving}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {rolesError && <Alert severity="error">{t('rolesLoadFailed')}</Alert>}
          <TextField
            label={t('email')}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); markDirty(); }}
            required
            autoFocus
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('firstName')}
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); markDirty(); }}
              fullWidth
            />
            <TextField
              label={t('lastName')}
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); markDirty(); }}
              fullWidth
            />
          </Stack>
          <TextField
            label={t('role')}
            select
            value={roleId}
            onChange={(e) => { setRoleId(e.target.value); markDirty(); }}
            required
            disabled={rolesLoading || assignableRoles.length === 0}
          >
            {assignableRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {roleDefinitionLabel(role, t)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
